import BubbleButtonComponent, {
  BubbleType
} from '@spa/components/bubble-button/bubble-button.component';
import React, { ReactNode, memo } from 'react';
import ConfigurableCmsComponentMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-component-metadata.component';
import ConfigurableCmsSlotMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-slot-metadata.component';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import HeadlineComponent from '@spa/components/headline/headline.component';
import { LinkComponentLink } from '@spa/components/link/link.component';
import { LinkTarget } from '@spa/lib/react/hooks/useLinkAttributes';
import { Metadata } from '@spa/pages-converters/cms/component-envelops/CmsComponentEnvelopBase';
import styles from './text-link-buttons.component.module.scss';

interface TextLinkButtonsItem {
  readonly link: LinkComponentLink | null;
  readonly text: string;
  readonly dateFrom: number | null;
  readonly dateTo: number | null;
  readonly metadata: ReadonlyArray<Metadata>;
}

export interface Props {
  readonly id: string;
  readonly headline: string | null;
  readonly subHeadline: string | null;
  readonly headlineLink: LinkComponentLink | null;
  readonly dateFrom: number | null;
  readonly dateTo: number | null;
  readonly items: ReadonlyArray<TextLinkButtonsItem>;
  readonly itemsMetadata: ReadonlyArray<Metadata>;
  readonly editorMode?: boolean;
}

function TextLinkButtonsComponent({
  id,
  headline,
  subHeadline,
  headlineLink,
  dateFrom,
  dateTo,
  items,
  itemsMetadata,
  editorMode = false
}: Props): ReactNode {
  return (
    <div className={styles['text-link-buttons']}>
      {editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />}
      {headline && (
        <HeadlineComponent
          id={`${id}-headline`}
          headline={headline}
          subHeadline={subHeadline}
          link={headlineLink}
        />
      )}

      {editorMode && <ConfigurableCmsSlotMetadata beforeSlot={true} metadata={itemsMetadata} />}
      <div className={styles['text-link-buttons__buttons-wrapper']}>
        <div className={styles['text-link-buttons__buttons']}>
          {items.map(
            ({
              link,
              dateTo: itemDateTo,
              dateFrom: itemDateFrom,
              metadata: itemMetadata,
              text
            }: TextLinkButtonsItem) => {
              if (link) {
                const isHighlighted: boolean = link?.target === LinkTarget.SELF;

                return (
                  <div key={text}>
                    {editorMode && (
                      <>
                        <ConfigurableCmsComponentMetadata
                          beforeComponent={true}
                          metadata={itemMetadata}
                        />
                        <ConfigurableCmsValidity dateFrom={itemDateFrom} dateTo={itemDateTo} />
                      </>
                    )}
                    <div className={'text-link-buttons__button'}>
                      <BubbleButtonComponent
                        link={link}
                        type={BubbleType.LINK}
                        isHighlighted={isHighlighted}
                        test-dataId={'bubble-button'}
                        text={text}
                      />
                    </div>
                    {editorMode && (
                      <ConfigurableCmsComponentMetadata
                        beforeComponent={false}
                        metadata={itemMetadata}
                      />
                    )}
                  </div>
                );
              }
            }
          )}
        </div>
      </div>
      {editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={itemsMetadata} />}
    </div>
  );
}

export default memo(TextLinkButtonsComponent);
